import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Plus, Trash2, HelpCircle } from "lucide-react";
import { api } from "@/lib/api";
import { pricingApi } from "@/lib/phase3Entities";
import { apiError } from "@/lib/errors";
import { PRICING_CATALOG, PRICING_SECTIONS, catalogBySection, catalogVisibleBySection, catalogByKey, deriveQty, findPresetByRate } from "./pricingCatalog";
import { coachOptionLabel } from "@/lib/assetDisplay";

// Accordion order — driven by the catalog. New rates & sections effective 2026-01-13.
const SECTIONS = PRICING_SECTIONS;

const BILL_TO = [
  { value: "customer",    label: "Client" },
  { value: "all_access",  label: "All Access" },
  { value: "third_party", label: "Driver Collect" },
];

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : usd.format(n);
};

// Msg 626 visual fix: pretty-format the unit column so a Payroll Fee line
// running as a percentage renders "Percentage" instead of the catalog's
// static "flat" default. Falls back to normalizing per_day → "per day", etc.
const formatUnitLabel = (u) => {
  if (!u) return "";
  const key = String(u).toLowerCase();
  if (key === "percent") return "Percentage";
  if (key === "flat") return "Flat";
  return key.replace(/_/g, " ");
};

const OPEN_KEY = (tid) => `aa_pricing_open__${tid}`;
const safeParse = (s) => { try { return s ? JSON.parse(s) : null; } catch { return null; } };

export default function PricingTab({ tourId, canWrite = true, tourAssets = null, allAssets = null, tourDrivers = null }) {
  const [summary, setSummary] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [openMap, setOpenMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null); // catalog key currently mutating

  const load = useCallback(async () => {
    try {
      const [s, d] = await Promise.all([
        pricingApi.summary(tourId),
        api.get(`/tours/${tourId}/pricing/derived-defaults`).then((r) => r.data).catch(() => null),
      ]);
      setSummary(s);
      setDefaults(d);
      const stored = safeParse(localStorage.getItem(OPEN_KEY(tourId)));
      // Open sections that have persisted line items OR contain catalog rows
      // (which is now every section — so default = mostly open).
      const initial = stored || {};
      if (!stored) {
        SECTIONS.forEach((sec) => { initial[sec.key] = itemsFor(s, sec).length > 0 || sec.key === "equipment"; });
      }
      setOpenMap(initial);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setLoading(false); }
  }, [tourId]);
  useEffect(() => { load(); }, [load]);

  const persistOpen = (next) => {
    setOpenMap(next);
    try { localStorage.setItem(OPEN_KEY(tourId), JSON.stringify(next)); } catch { /* noop */ }
  };
  const toggleSection = (k) => persistOpen({ ...openMap, [k]: !openMap[k] });

  const refresh = useCallback(async () => {
    try { setSummary(await pricingApi.summary(tourId)); }
    catch (e) { toast.error(apiError(e)); }
  }, [tourId]);

  // Owner-reported mobile defect fix moved UP to
  // `PricingAndQuoteWorkspace` (2026-09-10 re-fix). That component owns
  // the vertical stack (PricingTab + QuoteSummaryCard + QuoteActions +
  // Quote Versions) and applies a single ResizeObserver-driven
  // padding-bottom that covers ALL siblings below the pinned totals
  // footer. This tab therefore no longer needs its own padding hook.

  if (loading) return <div className="text-steel text-[13px] p-4">Loading pricing…</div>;

  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-6" data-testid="pricing-tab">
      <div className="space-y-2" data-testid="pricing-tab-content">
        {SECTIONS.map((sec) => (
          <Section
            key={sec.key}
            section={sec}
            summary={summary}
            defaults={defaults}
            open={!!openMap[sec.key]}
            onToggle={() => toggleSection(sec.key)}
            canWrite={canWrite}
            busyKey={busyKey}
            setBusyKey={setBusyKey}
            refresh={refresh}
            tourAssets={tourAssets}
            allAssets={allAssets}
            tourDrivers={tourDrivers}
          />
        ))}
      </div>
      <TotalsCard summary={summary} />
    </div>
  );
}

function itemsFor(summary, sec) {
  if (!summary?.line_items) return [];
  // Wave 4B-Q · duplication fix. If a line has a `category_key`, ONLY
  // match by key (never fall back to category). This prevents Custom
  // Charges (category_key="custom_charge", category="misc") from being
  // pulled into BOTH the Fees section AND the Miscellaneous section by
  // the raw category-match fallback that historically drove the bug.
  const sectionKeySet = new Set(catalogBySection(sec.key).map((r) => r.key));
  const catalogSectionKeys = new Set(catalogBySection(sec.key).map((r) => r.category));
  return summary.line_items.filter((li) => {
    if (li.category_key) {
      return sectionKeySet.has(li.category_key);
    }
    return catalogSectionKeys.has(li.category);
  });
}

// Rows in Miscellaneous that don't match any catalog key = orphan custom charges.
function orphansForMisc(summary) {
  if (!summary?.line_items) return [];
  const knownCategories = new Set(PRICING_CATALOG.map((r) => r.category));
  return summary.line_items.filter(
    (li) => !li.category_key && (li.category === "misc" || li.category === "other" || !knownCategories.has(li.category)),
  );
}

// Canonical unit catalog — shared source for every Quote pricing line
// that supports operator-editable Unit selection (Payroll Fee + Custom
// Charge). Mirrors the `unit_of_measure` Literal on `TourPricingLineItem`
// in backend/models/phase3.py.
export const CANONICAL_UNIT_OPTIONS = [
  "per_day", "per_week", "per_month", "per_mile",
  "per_trip", "each", "flat", "percent",
];

// ---------------- Section ----------------
const Section = ({ section, summary, defaults, open, onToggle, canWrite, busyKey, setBusyKey, refresh, tourAssets, allAssets, tourDrivers }) => {
  // Wave 4B-Q · hidden catalog rows (e.g. legacy cleaning services) are
  // excluded from new-Quote selection but still counted when a
  // historical persisted line references them.
  const catalog = catalogVisibleBySection(section.key);
  const persisted = itemsFor(summary, section);
  // Reconcile: match each catalog row with its persisted line by category_key
  // OR by category fallback (first-match wins per catalog row).
  const persistedByKey = new Map();
  const persistedByCategoryLeftovers = [...persisted];
  for (const li of persisted) {
    if (li.category_key) persistedByKey.set(li.category_key, li);
  }
  // Remove keyed matches from the fallback pool.
  const remaining = persistedByCategoryLeftovers.filter(
    (li) => !li.category_key || !persistedByKey.has(li.category_key),
  );
  const takeByCategory = (cat) => {
    const idx = remaining.findIndex((li) => li.category === cat);
    if (idx === -1) return null;
    return remaining.splice(idx, 1)[0];
  };

  const catalogRows = catalog.map((row) => {
    const li = persistedByKey.get(row.key) || takeByCategory(row.category);
    return { catalog: row, line: li || null };
  });

  // For the custom/misc section: any remaining unmatched persisted lines become
  // "orphan custom charges" surfaced so nothing disappears.
  const orphanCharges = section.key === "custom" ? orphansForMisc(summary).filter((o) => !catalogRows.some((r) => r.line?.id === o.id)) : [];
  // Filter orphans out of the "remaining" bucket already? Yes — orphansForMisc filters by category_key absence.
  const extraRemaining = section.key === "custom"
    ? remaining.filter((li) => !orphanCharges.some((o) => o.id === li.id))
    : remaining;

  const sectionSubtotal =
    catalogRows.reduce((acc, r) => acc + Number(r.line?.computed_amount ?? 0), 0) +
    orphanCharges.reduce((a, o) => a + Number(o.computed_amount ?? 0), 0) +
    extraRemaining.reduce((a, o) => a + Number(o.computed_amount ?? 0), 0);

  return (
    <div className="bg-white border border-coach-silver rounded-md overflow-hidden"
      data-testid={`pricing-section-${section.key}`}>
      <button type="button" onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-road-white text-left"
        data-testid={`pricing-section-toggle-${section.key}`}>
        <div className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown size={14} className="text-steel shrink-0" /> : <ChevronRight size={14} className="text-steel shrink-0" />}
          <span className="font-display text-[14px] text-carbon truncate">{section.label}</span>
          {section.internal && (
            <span className="text-[10px] uppercase tracking-widest text-amber-hold border border-amber-hold rounded-sm px-1.5 py-0.5 ml-1">Internal</span>
          )}
          <span className="text-[11px] text-steel">
            {catalogRows.filter((r) => !!r.line).length + orphanCharges.length + extraRemaining.length} of {catalogRows.length}{section.key === "custom" ? "+" : ""} enabled
          </span>
        </div>
        <div className="tabular font-medium text-[13px] text-carbon whitespace-nowrap">{usd.format(sectionSubtotal)}</div>
      </button>

      {open && (
        <div className="border-t border-coach-silver">
          <table className="w-full text-[12.5px]">
            <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
              <tr>
                <th className="text-left px-2 py-1.5 w-8"></th>
                <th className="text-left px-3 py-1.5">Item</th>
                <th className="text-right px-2 py-1.5 w-20">Qty</th>
                <th className="text-left px-2 py-1.5 w-20">Unit</th>
                <th className="text-right px-2 py-1.5 w-24">Rate</th>
                <th className="text-right px-2 py-1.5 w-24">Amount</th>
                <th className="text-left px-2 py-1.5 w-28">Billing</th>
              </tr>
            </thead>
            <tbody data-testid={`pricing-rows-${section.key}`}>
              {catalogRows.map(({ catalog, line }) => (
                <CatalogRow
                  key={catalog.key}
                  tourId={summary.tour_id}
                  catalog={catalog}
                  line={line}
                  defaults={defaults}
                  canWrite={canWrite}
                  busy={busyKey === catalog.key}
                  setBusyKey={setBusyKey}
                  refresh={refresh}
                  tourAssets={tourAssets}
                  allAssets={allAssets}
                  tourDrivers={tourDrivers}
                />
              ))}
              {orphanCharges.map((o) => (
                <OrphanRow key={o.id} line={o} canWrite={canWrite} refresh={refresh} />
              ))}
              {extraRemaining.map((o) => (
                <OrphanRow key={o.id} line={o} canWrite={canWrite} refresh={refresh} />
              ))}
            </tbody>
          </table>
          {section.allowCustom && canWrite && (
            <AddCustomCharge tourId={summary.tour_id} refresh={refresh}
              tourAssets={tourAssets} allAssets={allAssets} />
          )}
          {section.notes && section.notes.length > 0 && (
            <div
              className={`border-t border-coach-silver px-4 py-2 space-y-1 ${section.notesInternal ? "bg-amber-hold/10" : "bg-road-white"}`}
              data-testid={`pricing-section-notes-${section.key}`}
            >
              {section.notesInternal && (
                <div className="text-[10px] uppercase tracking-widest text-amber-hold font-medium">
                  Internal note — not shown on customer PDF
                </div>
              )}
              {section.notes.map((n, i) => (
                <div key={i} className="text-[11.5px] text-carbon leading-snug">{n}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------- Catalog row ----------------
const CatalogRow = ({ tourId, catalog, line, defaults, canWrite, busy, setBusyKey, refresh, tourAssets, allAssets, tourDrivers }) => {
  const enabled = !!line;
  const [draft, setDraft] = useState(() => ({
    quantity: line?.quantity ?? deriveQty(catalog, defaults),
    rate: line?.rate ?? catalog.defaultRate ?? "",
    billing: line?.billing_responsibility ?? catalog.defaultBilling,
    cfd: line?.customer_facing_description ?? "",
    presetKey: (line?.rate && catalog.presets)
      ? (findPresetByRate(catalog.presets, line.rate)?.key ?? "")
      : "",
  }));

  useEffect(() => {
    if (!enabled) {
      setDraft((d) => ({
        ...d,
        quantity: deriveQty(catalog, defaults),
        // Only pre-seed rate from catalog default when the operator hasn't
        // already started typing something in this session.
        rate: d.rate === "" ? (catalog.defaultRate ?? "") : d.rate,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults?.billed_days, defaults?.estimated_mileage]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const choosePreset = (presetKey) => {
    const p = (catalog.presets || []).find((x) => x.key === presetKey);
    if (!p) { setDraft((d) => ({ ...d, presetKey: "" })); return; }
    setDraft((d) => ({ ...d, presetKey, rate: p.rate }));
    // If the row is already enabled, live-commit the new rate.
    if (enabled && line?.id) {
      commitPatch({ rate: p.rate });
    }
  };

  const enable = async () => {
    if (!canWrite) return;
    // Require a non-zero rate to persist (do not create $0 zombie rows).
    const rate = String(draft.rate ?? "").trim();
    if (!rate || Number(rate) === 0) {
      toast.error("Enter a rate before enabling this line.");
      return;
    }
    setBusyKey(catalog.key);
    try {
      await pricingApi.add(tourId, {
        category: catalog.category,
        category_key: catalog.key,
        description: catalog.label,
        customer_facing_description: draft.cfd || catalog.label,
        quantity: String(draft.quantity || 1),
        unit: (catalog.unit_of_measure || "flat").replace(/^per_/, ""),
        unit_of_measure: catalog.unit_of_measure,
        rate,
        billing_responsibility: draft.billing,
        customer_visible: !catalog.internal,
      });
      await refresh();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusyKey(null); }
  };

  const disable = async () => {
    if (!line?.id) return;
    if (!window.confirm(`Remove ${catalog.label} from this tour's pricing?`)) return;
    setBusyKey(catalog.key);
    try { await pricingApi.remove(line.id); await refresh(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusyKey(null); }
  };

  const commitPatch = async (patch) => {
    if (!line?.id) return;
    setBusyKey(catalog.key);
    try { await pricingApi.patch(line.id, patch); await refresh(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusyKey(null); }
  };

  const missingDeriveHint = !enabled && !draft.quantity && catalog.deriveType !== "flat" && (
    catalog.deriveType === "mileage"
      ? "Set estimated mileage to auto-fill"
      : "Set tour dates to auto-fill days"
  );

  // Assignment context subtitle (Coach Lease → coach name, Trailer Lease →
  // trailer name, Driver Days → primary driver name). Display-only.
  // Finalization Phase F1 (Owner Rule J): Coach Name leads; fleet number
  // is not surfaced here.
  let assignmentSubtitle = null;
  if (allAssets && tourAssets) {
    if (catalog.key === "coach_lease") {
      const coaches = tourAssets
        .map((a) => allAssets.find((x) => x.id === a.asset_id))
        .filter((a) => a && a.asset_type !== "trailer");
      if (coaches.length) assignmentSubtitle = coaches.map((c) => c.unit_name || c.display_name || c.fleet_number || "Entertainer Coach").join(" · ");
    }
    if (catalog.key === "trailer_lease") {
      const trailers = tourAssets
        .map((a) => allAssets.find((x) => x.id === a.asset_id))
        .filter((a) => a && a.asset_type === "trailer");
      if (trailers.length) assignmentSubtitle = trailers.map((c) => c.unit_name || c.display_name || c.fleet_number || "Trailer").join(" · ");
    }
  }
  if (catalog.key === "driver_days" && tourDrivers && tourDrivers.length) {
    const primary = tourDrivers.find((d) => (d.role || "").toLowerCase().includes("primary")) || tourDrivers[0];
    if (primary) assignmentSubtitle = primary.driver_name || primary.driver_id?.slice(0, 6);
  }

  // Projected amount (display-only) — computed live from qty * rate BEFORE
  // the row is enabled. Never sent to the server.
  const projectedAmount = (() => {
    if (enabled) return null;
    const q = Number(draft.quantity);
    const r = Number(draft.rate);
    if (!Number.isFinite(q) || !Number.isFinite(r) || q <= 0 || r <= 0) return null;
    return q * r;
  })();

  return (
    <tr data-testid={`pricing-catalog-row-${catalog.key}`}
      className={`border-t border-coach-silver/50 ${enabled ? "" : "text-steel"}`}>
      <td className="px-2 py-1.5">
        <input
          type="checkbox"
          checked={enabled}
          disabled={!canWrite || busy}
          onChange={enabled ? disable : enable}
          data-testid={`pricing-enable-${catalog.key}`}
          className="accent-coach-blue"
          title={enabled ? "Remove from tour pricing" : "Add to tour pricing"}
        />
      </td>
      <td className="px-3 py-1.5">
        <div className="flex items-center gap-1">
          <span className={enabled ? "text-carbon" : ""}>{catalog.label}</span>
          {catalog.hint && (
            <span title={catalog.hint}>
              <HelpCircle size={11} className="text-steel" />
            </span>
          )}
        </div>
        {assignmentSubtitle && (
          <div className="text-[10.5px] text-coach-blue mt-0.5" data-testid={`pricing-assignment-${catalog.key}`}>
            {assignmentSubtitle}
          </div>
        )}
        {catalog.presets && (
          <div className="mt-1">
            <select
              value={draft.presetKey}
              disabled={!canWrite || busy}
              onChange={(e) => choosePreset(e.target.value)}
              data-testid={`pricing-preset-${catalog.key}`}
              className="text-[11px] border border-coach-silver rounded-sm px-1.5 py-0.5 bg-white text-carbon"
            >
              <option value="">Rate Type…</option>
              {catalog.presets.map((p) => (
                <option key={p.key} value={p.key}>{p.label} — ${p.rate}/day</option>
              ))}
            </select>
          </div>
        )}
        {catalog.note && (
          <div className="text-[10.5px] text-steel italic mt-0.5">{catalog.note}</div>
        )}
        {missingDeriveHint && (
          <div className="text-[10.5px] text-amber-hold italic">{missingDeriveHint}</div>
        )}
      </td>
      <td className="px-2 py-1.5 text-right">
        {catalog.key === "payroll_fee" ? (
          <span
            data-testid={`pricing-qty-${catalog.key}`}
            aria-readonly="true"
            className="inline-block w-full text-right tabular text-[12px] text-carbon"
            title="Payroll Fee quantity is locked to 1 (canonical business rule)"
          >
            1
          </span>
        ) : (
          <input
            value={draft.quantity ?? ""}
            onChange={(e) => set("quantity", e.target.value)}
            onBlur={() => enabled && line?.quantity !== draft.quantity && commitPatch({ quantity: String(draft.quantity || 1) })}
            disabled={!canWrite || busy}
            data-testid={`pricing-qty-${catalog.key}`}
            className={`w-full text-right tabular border rounded-sm px-1.5 py-1 text-[12px] ${enabled ? "border-coach-silver" : "border-transparent bg-transparent"}`}
          />
        )}
      </td>
      <td className="px-2 py-1.5 text-steel" data-testid={`pricing-unit-${catalog.key}`}>
        {catalog.key === "payroll_fee" ? (
          <span
            data-testid="pricing-unit-select-payroll_fee"
            aria-readonly="true"
            className="inline-block text-[11.5px] text-carbon"
            title="Payroll Fee unit is locked to Percentage (canonical business rule)"
          >
            Percentage
          </span>
        ) : (
          formatUnitLabel(line?.unit_of_measure || line?.unit || catalog.unit_of_measure)
        )}
      </td>
      <td className="px-2 py-1.5 text-right">
        {catalog.key === "payroll_fee" ? (
          <span
            data-testid={`pricing-rate-${catalog.key}`}
            aria-readonly="true"
            className="inline-block w-full text-right tabular text-[12px] text-carbon"
            title="Payroll Fee rate is locked to 28% (canonical business rule)"
          >
            28%
          </span>
        ) : (
          <input
            value={draft.rate ?? ""}
            onChange={(e) => set("rate", e.target.value)}
            onBlur={() => enabled && line?.rate !== draft.rate && commitPatch({ rate: String(draft.rate || 0) })}
            disabled={!canWrite || busy}
            placeholder={enabled ? "" : "Add rate"}
            data-testid={`pricing-rate-${catalog.key}`}
            className={`w-full text-right tabular border rounded-sm px-1.5 py-1 text-[12px] ${enabled ? "border-coach-silver" : "border-coach-silver/40 bg-transparent placeholder:text-steel/60"}`}
          />
        )}
      </td>
      <td className="px-2 py-1.5 text-right tabular font-medium"
        data-testid={`pricing-amount-${catalog.key}`}>
        {enabled
          ? fmt(line?.computed_amount)
          : projectedAmount !== null
            ? <span className="text-steel italic" title="Projected — not persisted until enabled">{fmt(projectedAmount)}</span>
            : <span className="text-steel">—</span>}
      </td>
      <td className="px-2 py-1.5">
        {enabled ? (
          <select
            value={draft.billing}
            disabled={!canWrite || busy}
            onChange={(e) => { set("billing", e.target.value); commitPatch({ billing_responsibility: e.target.value }); }}
            data-testid={`pricing-billing-${catalog.key}`}
            className="w-full border border-coach-silver rounded-sm px-1.5 py-1 text-[11.5px]"
          >
            {BILL_TO.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        ) : (
          <span className="text-steel text-[11px] italic">
            {(BILL_TO.find((b) => b.value === catalog.defaultBilling) || {}).label}
          </span>
        )}
      </td>
    </tr>
  );
};

// ---------------- Orphan (legacy or custom row) ----------------
const OrphanRow = ({ line, canWrite, refresh }) => {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!window.confirm(`Remove "${line.description}"?`)) return;
    setBusy(true);
    try { await pricingApi.remove(line.id); await refresh(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <tr data-testid={`pricing-orphan-${line.id}`} className="border-t border-coach-silver/50 bg-road-white/40">
      <td className="px-2 py-1.5"><span className="text-steel text-[10px]">custom</span></td>
      <td className="px-3 py-1.5 text-carbon">
        <div className="truncate">{line.description}</div>
        {line.notes && <div className="text-[10.5px] text-amber-hold truncate">Note: {line.notes}</div>}
      </td>
      <td className="px-2 py-1.5 text-right tabular text-carbon">{line.quantity}</td>
      <td className="px-2 py-1.5 text-steel">{formatUnitLabel(line.unit_of_measure || line.unit)}</td>
      <td className="px-2 py-1.5 text-right tabular text-carbon">{fmt(line.rate)}</td>
      <td className="px-2 py-1.5 text-right tabular font-medium">{fmt(line.computed_amount)}</td>
      <td className="px-2 py-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-steel text-[11px]">
            {(BILL_TO.find((b) => b.value === line.billing_responsibility) || {}).label || line.billing_responsibility}
          </span>
          {canWrite && (
            <button title="Remove" disabled={busy} onClick={remove}
              data-testid={`pricing-orphan-remove-${line.id}`}
              className="text-steel hover:text-critical-red">
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

// ---------------- Add Custom Charge (Miscellaneous only) ----------------
// Wave 4B-Q · duplication fix (Section 16): every Custom Charge is
// stamped with `category_key="custom_charge"` so `itemsFor()` matches it
// EXCLUSIVELY to the Miscellaneous & Other section — no fall-through
// into Fees.
// Adds `Percentage` unit + explicit `Percentage Of` base selector (Q2)
// and explicit `Taxable` toggle (default No).
const PERCENT_BASES = [
  { value: "unit_subtotal",     label: "Unit Subtotal" },
  { value: "driver_wages",      label: "Driver Wages" },
  { value: "coach_lease",       label: "Coach Lease" },
  { value: "taxable_subtotal",  label: "Taxable Subtotal" },
  { value: "quote_subtotal",    label: "Quote Subtotal (Grand Pre-Tax Subtotal)" },
];
const AddCustomCharge = ({ tourId, refresh, tourAssets = null, allAssets = null }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    description: "", quantity: "1", unit_of_measure: "flat", rate: "",
    billing_responsibility: "customer", customer_facing_description: "",
    percentage_of_base: "", taxable: false, unit_group_id: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isPercent = form.unit_of_measure === "percent";

  // Bus options = each coach assignment on the tour. Empty = Quote-level.
  // Finalization Phase F1 (Owner Rules J + 5): show Coach Name; append
  // fleet# only when another visible coach shares the same name.
  const busOptions = React.useMemo(() => {
    if (!tourAssets || !allAssets) return [];
    const coachAssets = tourAssets
      .map((a) => {
        const asset = allAssets.find((x) => x.id === a.asset_id);
        if (!asset || asset.asset_type === "trailer") return null;
        return { assignment_id: a.id, asset };
      })
      .filter(Boolean);
    const siblings = coachAssets.map((x) => x.asset);
    return coachAssets.map((x) => ({
      assignment_id: x.assignment_id,
      label: coachOptionLabel(x.asset, siblings),
    }));
  }, [tourAssets, allAssets]);

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        className="w-full px-4 py-2 border-t border-coach-silver text-[12.5px] text-coach-blue hover:bg-road-white inline-flex items-center justify-center gap-1"
        data-testid="pricing-add-custom-charge">
        <Plus size={12} /> Add Custom Charge
      </button>
    );
  }
  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) return toast.error("Description required");
    if (isPercent && !form.percentage_of_base) {
      return toast.error("Choose what this percentage applies to.");
    }
    setBusy(true);
    try {
      const body = {
        category: "misc",
        category_key: "custom_charge",
        description: form.description,
        customer_facing_description: form.customer_facing_description || form.description,
        quantity: isPercent ? "1" : (form.quantity || "1"),
        unit: isPercent ? "percent" : (form.unit_of_measure || "flat").replace(/^per_/, ""),
        unit_of_measure: form.unit_of_measure,
        rate: form.rate || "0",
        billing_responsibility: form.billing_responsibility,
        taxable: form.taxable ? true : false,
      };
      if (isPercent) {
        body.percentage_of_base = form.percentage_of_base;
        // Wave 4B-Q · CP4B · scope resolves per operator selection.
        // If a Bus is chosen → unit-scope (base reads only that Bus's
        // aggregates). Otherwise Quote-scope.
        body.percentage_scope = form.unit_group_id ? "unit" : "quote";
      }
      if (form.unit_group_id) body.unit_group_id = form.unit_group_id;
      await pricingApi.add(tourId, body);
      await refresh();
      setOpen(false);
      setForm({ description: "", quantity: "1", unit_of_measure: "flat", rate: "",
                billing_responsibility: "customer", customer_facing_description: "",
                percentage_of_base: "", taxable: false, unit_group_id: "" });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="border-t border-coach-silver bg-road-white px-3 py-2 grid grid-cols-2 md:grid-cols-12 gap-2 items-end"
      data-testid="pricing-add-form-miscellaneous">
      <label className="col-span-2 md:col-span-4">
        <div className="text-[10px] uppercase tracking-widest text-steel mb-0.5">Description</div>
        <input required autoFocus value={form.description} onChange={(e) => set("description", e.target.value)}
          data-testid="pricing-add-desc-miscellaneous"
          className="w-full border border-coach-silver rounded-sm px-1.5 py-1 text-[12px]" />
      </label>
      <label className="col-span-1">
        <div className="text-[10px] uppercase tracking-widest text-steel mb-0.5">Qty</div>
        <input value={form.quantity} onChange={(e) => set("quantity", e.target.value)}
          disabled={isPercent}
          data-testid="pricing-add-qty-miscellaneous"
          className="w-full border border-coach-silver rounded-sm px-1.5 py-1 text-[12px] text-right tabular disabled:bg-chrome-mist" />
      </label>
      <label className="col-span-1 md:col-span-2">
        <div className="text-[10px] uppercase tracking-widest text-steel mb-0.5">Unit</div>
        <select value={form.unit_of_measure} onChange={(e) => set("unit_of_measure", e.target.value)}
          data-testid="pricing-add-unit-miscellaneous"
          className="w-full border border-coach-silver rounded-sm px-1.5 py-1 text-[12px]">
          {CANONICAL_UNIT_OPTIONS.map((u) => <option key={u} value={u}>{formatUnitLabel(u)}</option>)}
        </select>
      </label>
      <label className="col-span-1">
        <div className="text-[10px] uppercase tracking-widest text-steel mb-0.5">{isPercent ? "Percent" : "Rate"}</div>
        <input value={form.rate} onChange={(e) => set("rate", e.target.value)}
          data-testid="pricing-add-rate-miscellaneous"
          className="w-full border border-coach-silver rounded-sm px-1.5 py-1 text-[12px] text-right tabular" />
      </label>
      {isPercent && (
        <label className="col-span-2 md:col-span-3">
          <div className="text-[10px] uppercase tracking-widest text-steel mb-0.5">Percentage Of</div>
          <select required value={form.percentage_of_base} onChange={(e) => set("percentage_of_base", e.target.value)}
            data-testid="pricing-add-percent-base"
            className="w-full border border-coach-silver rounded-sm px-1.5 py-1 text-[12px]">
            <option value="">Choose base…</option>
            {PERCENT_BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </label>
      )}
      <label className="col-span-2 md:col-span-2">
        <div className="text-[10px] uppercase tracking-widest text-steel mb-0.5">Billing</div>
        <select value={form.billing_responsibility} onChange={(e) => set("billing_responsibility", e.target.value)}
          data-testid="pricing-add-billing-miscellaneous"
          className="w-full border border-coach-silver rounded-sm px-1.5 py-1 text-[12px]">
          {BILL_TO.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
      </label>
      {busOptions.length > 0 && (
        <label className="col-span-2 md:col-span-3">
          <div className="text-[10px] uppercase tracking-widest text-steel mb-0.5">Bus / Scope</div>
          <select value={form.unit_group_id} onChange={(e) => set("unit_group_id", e.target.value)}
            data-testid="pricing-add-unit-group-miscellaneous"
            className="w-full border border-coach-silver rounded-sm px-1.5 py-1 text-[12px]">
            <option value="">Quote-Level (no Bus)</option>
            {busOptions.map((b) => <option key={b.assignment_id} value={b.assignment_id}>{b.label}</option>)}
          </select>
        </label>
      )}
      <label className="col-span-1 md:col-span-2 inline-flex items-center gap-1.5 text-[12px] pt-4">
        <input type="checkbox" checked={form.taxable} onChange={(e) => set("taxable", e.target.checked)}
          data-testid="pricing-add-taxable-miscellaneous" />
        <span className="text-steel uppercase text-[10px] tracking-widest">Taxable (9.75%)</span>
      </label>
      <div className="col-span-2 md:col-span-1 flex gap-1 justify-end">
        <button type="button" onClick={() => setOpen(false)} disabled={busy}
          className="px-2 py-1 border border-coach-silver rounded-sm text-[12px] text-steel">Cancel</button>
        <button type="submit" disabled={busy}
          data-testid="pricing-add-submit-miscellaneous"
          className="px-3 py-1 bg-coach-blue text-white rounded-sm text-[12px] font-medium disabled:opacity-60">
          {busy ? "…" : "Add"}
        </button>
      </div>
    </form>
  );
};

// ---------------- Totals ----------------
const TotalsCard = ({ summary }) => {
  const totals = [
    { k: "customer_total",  label: "Subtotal (Client, pre-tax)" },
    { k: "contract_total",  label: "Included in Contract" },
    { k: "driver_collect",  label: "Driver Collect" },
    { k: "taxable_subtotal",label: "Taxable Subtotal" },
    { k: "tax_amount",      label: "Sales Tax (9.75%)" },
    { k: "internal_cost",   label: "Internal Cost" },
  ];
  const grand = summary?.grand_total ?? summary?.customer_total;
  return (
    <aside
      className="fixed left-0 right-0 bottom-0 z-30 lg:sticky lg:top-6 lg:self-start lg:right-auto lg:bottom-auto lg:left-auto bg-white border-t lg:border border-coach-silver lg:rounded-md p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] lg:pb-4"
      data-testid="pricing-totals-card">
      <div className="text-[10.5px] uppercase tracking-widest text-steel mb-2">Tour Budget</div>
      <div className="grid grid-cols-3 lg:grid-cols-1 gap-3 lg:gap-2">
        {totals.map((row, i) => (
          <div key={`${row.k}-${i}`} className="flex items-baseline justify-between border-b border-coach-silver/50 pb-1">
            <span className="text-[11.5px] text-steel">{row.label}</span>
            <span className="tabular text-[12.5px] text-carbon font-medium" data-testid={`totals-${row.k}-${i}`}>
              {fmt(summary?.[row.k])}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-baseline justify-between">
        <span className="text-[11px] uppercase tracking-widest text-steel">Grand Total</span>
        <span className="tabular font-display text-[20px] lg:text-[22px] text-deep-road-navy"
          data-testid="totals-grand-total">
          {fmt(grand)}
        </span>
      </div>
      <div className="mt-1 text-[10.5px] text-steel">Server-computed · Decimal · ROUND_HALF_EVEN · Tax internal (no QB)</div>
    </aside>
  );
};
